import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { CheckCircle, Clock, Play, ArrowLeft, ArrowRight, Download, Brain, Edit3, Save, FileDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Tutorial {
  id: string;
  title: string;
  description: string;
  duration_minutes: number;
  difficulty: string;
  category: string;
  is_active: boolean;
}

interface TutorialStep {
  id: string;
  tutorial_id: string;
  title: string;
  description: string;
  step_order: number;
  content: any;
  is_active: boolean;
}

interface TutorialProgress {
  id: string;
  user_id: string;
  tutorial_id: string;
  step_id: string;
  completed_at: string | null;
}

interface TutorialPlayerProps {
  tutorial: Tutorial;
  onClose: () => void;
  onProgressUpdate?: (tutorialId: string, completedSteps: string[]) => void;
}

export function TutorialPlayer({ tutorial, onClose, onProgressUpdate }: TutorialPlayerProps) {
  const [steps, setSteps] = useState<TutorialStep[]>([]);
  const [progress, setProgress] = useState<TutorialProgress[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [activeAccordionValue, setActiveAccordionValue] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [worksheetData, setWorksheetData] = useState<Record<string, Record<string, string>>>({});
  const [evaluations, setEvaluations] = useState<Record<string, any>>({});
  const [isEvaluating, setIsEvaluating] = useState<Record<string, boolean>>({});
  const { toast } = useToast();

  useEffect(() => {
    loadTutorialData();
  }, [tutorial.id]);

  // Set active accordion value when steps load or current step changes
  useEffect(() => {
    if (steps.length > 0) {
      setActiveAccordionValue(steps[currentStepIndex]?.id || steps[0]?.id);
    }
  }, [steps, currentStepIndex]);

  const loadTutorialData = async () => {
    try {
      setLoading(true);

      // Create comprehensive tutorial content based on tutorial ID
      const tutorialContent = {
        'getting-started': [
          {
            id: 'step-1-getting-started',
            tutorial_id: tutorial.id,
            title: 'Understanding Your Audience',
            description: 'Learn how to identify and analyze your target audience for maximum impact',
            step_order: 1,
            content: { 
              html: `
                <div class="tutorial-content">
                  <h3 class="text-2xl font-bold mb-6 text-primary">Understanding Your Audience</h3>
                  
                  <div class="mb-8">
                    <p class="text-lg mb-4 leading-relaxed">The foundation of any successful course is understanding who you're teaching. This step will help you create learner personas and understand their needs, motivations, and challenges.</p>
                  </div>
                  
                  <div class="mb-8">
                    <h4 class="text-xl font-semibold mb-4 text-secondary-foreground">🎯 Key Questions to Ask:</h4>
                    <ol class="space-y-3 ml-6">
                      <li class="flex items-start gap-3">
                        <span class="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">1</span>
                        <span>What is their current knowledge level?</span>
                      </li>
                      <li class="flex items-start gap-3">
                        <span class="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">2</span>
                        <span>What are their learning goals?</span>
                      </li>
                      <li class="flex items-start gap-3">
                        <span class="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">3</span>
                        <span>What challenges do they face in their daily work?</span>
                      </li>
                      <li class="flex items-start gap-3">
                        <span class="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">4</span>
                        <span>How do they prefer to learn?</span>
                      </li>
                      <li class="flex items-start gap-3">
                        <span class="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">5</span>
                        <span>What constraints do they have (time, resources, technology)?</span>
                      </li>
                    </ol>
                  </div>
                  
                  <div class="mb-8">
                    <h4 class="text-xl font-semibold mb-4 text-secondary-foreground">📋 Practical Exercise:</h4>
                    <p class="mb-4 leading-relaxed">Create a learner persona using the template provided. Include demographics, goals, challenges, and learning preferences.</p>
                    
                    <div class="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg mb-6">
                      <div class="flex items-center mb-2">
                        <span class="text-2xl mr-2">💡</span>
                        <strong class="text-blue-800">Pro Tip:</strong>
                      </div>
                      <p class="text-blue-700 leading-relaxed">Survey potential learners before creating your course. Their input will save you time and increase engagement.</p>
                    </div>
                  </div>
                </div>
              `,
              worksheet: {
                title: "Audience Analysis Worksheet",
                fields: [
                  {
                    id: "target_audience",
                    label: "1. Describe your target audience (demographics, background, experience level):",
                    type: "textarea",
                    placeholder: "e.g., Elementary school teachers with 3-10 years experience, mostly female, ages 28-45..."
                  },
                  {
                    id: "learning_goals", 
                    label: "2. What are their primary learning goals?",
                    type: "textarea",
                    placeholder: "e.g., Improve classroom management, learn new teaching strategies..."
                  },
                  {
                    id: "daily_challenges",
                    label: "3. What challenges do they face in their daily work?",
                    type: "textarea", 
                    placeholder: "e.g., Behavior management, time constraints, diverse learning needs..."
                  },
                  {
                    id: "learning_preferences",
                    label: "4. How do they prefer to learn?",
                    type: "textarea",
                    placeholder: "e.g., Hands-on activities, peer collaboration, video demonstrations..."
                  },
                  {
                    id: "constraints",
                    label: "5. What constraints do they have?",
                    type: "textarea",
                    placeholder: "e.g., Limited time after school, varying technology access..."
                  }
                ]
              },
              resources: ['Audience Analysis Template', 'Survey Questions Guide']
            },
            is_active: true
          },
          {
            id: 'step-2-getting-started',
            tutorial_id: tutorial.id,
            title: 'Setting Learning Objectives',
            description: 'Create clear, measurable learning objectives using the SMART framework',
            step_order: 2,
            content: { 
              html: `
                <div class="tutorial-content">
                  <h3 class="text-2xl font-bold mb-6 text-primary">Setting Learning Objectives</h3>
                  
                  <div class="mb-8">
                    <p class="text-lg mb-4 leading-relaxed">Learning objectives are the foundation of your course. They define what learners will be able to do after completing your training.</p>
                  </div>
                  
                  <div class="mb-8">
                    <h4 class="text-xl font-semibold mb-4 text-secondary-foreground">🎯 SMART Objectives Framework:</h4>
                    <div class="grid gap-4">
                      <div class="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                        <span class="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">S</span>
                        <div>
                          <strong class="text-green-700">Specific:</strong>
                          <span class="ml-2">Clearly defined and focused</span>
                        </div>
                      </div>
                      <div class="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                        <span class="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">M</span>
                        <div>
                          <strong class="text-blue-700">Measurable:</strong>
                          <span class="ml-2">Can be assessed or evaluated</span>
                        </div>
                      </div>
                      <div class="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                        <span class="bg-purple-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">A</span>
                        <div>
                          <strong class="text-purple-700">Achievable:</strong>
                          <span class="ml-2">Realistic given the time and resources</span>
                        </div>
                      </div>
                      <div class="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                        <span class="bg-orange-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">R</span>
                        <div>
                          <strong class="text-orange-700">Relevant:</strong>
                          <span class="ml-2">Directly related to job performance</span>
                        </div>
                      </div>
                      <div class="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                        <span class="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">T</span>
                        <div>
                          <strong class="text-red-700">Time-bound:</strong>
                          <span class="ml-2">Can be accomplished within the course timeframe</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="mb-8">
                    <h4 class="text-xl font-semibold mb-4 text-secondary-foreground">⚡ Action Verbs to Use:</h4>
                    <p class="mb-4">Choose strong action verbs:</p>
                    <div class="flex flex-wrap gap-2 mb-4">
                      <span class="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm">analyze</span>
                      <span class="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm">create</span>
                      <span class="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm">design</span>
                      <span class="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm">implement</span>
                      <span class="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm">evaluate</span>
                      <span class="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm">synthesize</span>
                      <span class="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm">compare</span>
                      <span class="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm">demonstrate</span>
                    </div>
                  </div>
                  
                  <div class="mb-8">
                    <h4 class="text-xl font-semibold mb-4 text-secondary-foreground">📝 Example:</h4>
                    <div class="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg">
                      <p class="text-blue-800 italic leading-relaxed">"By the end of this module, participants will be able to <strong>create</strong> a differentiated lesson plan that includes at least three learning modalities for a specific grade level and subject area."</p>
                    </div>
                  </div>
                </div>
              `,
              worksheet: {
                title: "Learning Objectives Worksheet",
                fields: [
                  {
                    id: "course_topic",
                    label: "1. What is the main topic/subject of your course?",
                    type: "text",
                    placeholder: "e.g., Classroom Management for Elementary Teachers"
                  },
                  {
                    id: "objective_1",
                    label: "2. Write your first SMART learning objective:",
                    type: "textarea",
                    placeholder: "By the end of this course, participants will be able to..."
                  },
                  {
                    id: "objective_2", 
                    label: "3. Write your second SMART learning objective:",
                    type: "textarea",
                    placeholder: "By the end of this course, participants will be able to..."
                  },
                  {
                    id: "objective_3",
                    label: "4. Write your third SMART learning objective:",
                    type: "textarea",
                    placeholder: "By the end of this course, participants will be able to..."
                  },
                  {
                    id: "assessment_strategy",
                    label: "5. How will you assess if learners have met these objectives?",
                    type: "textarea",
                    placeholder: "e.g., Portfolio review, practical demonstration, quiz..."
                  }
                ]
              },
              resources: ['SMART Objectives Template', 'Learning Taxonomy Guide', 'Action Verb List']
            },
            is_active: true
          }
        ],
        'content-development': [
          {
            id: 'step-1-content-dev',
            tutorial_id: tutorial.id,
            title: 'Creating Engaging Materials',
            description: 'Learn how to develop materials that keep learners engaged and motivated',
            step_order: 1,
            content: { 
              html: `
                <div class="tutorial-content">
                  <h3 class="text-2xl font-bold mb-6 text-primary">Creating Engaging Materials</h3>
                  
                  <div class="mb-8">
                    <p class="text-lg mb-4 leading-relaxed">Engagement is key to learning retention. Your materials should capture attention, maintain interest, and promote active learning.</p>
                  </div>
                  
                  <div class="mb-8">
                    <h4 class="text-xl font-semibold mb-4 text-secondary-foreground">🎯 The AIDA Framework for Content:</h4>
                    <div class="grid gap-4">
                      <div class="flex items-start gap-4 p-4 bg-red-50 rounded-lg border-l-4 border-red-400">
                        <span class="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">A</span>
                        <div>
                          <strong class="text-red-700">Attention:</strong>
                          <span class="ml-2">Start with a compelling hook</span>
                        </div>
                      </div>
                      <div class="flex items-start gap-4 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                        <span class="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">I</span>
                        <div>
                          <strong class="text-blue-700">Interest:</strong>
                          <span class="ml-2">Connect to learner needs and goals</span>
                        </div>
                      </div>
                      <div class="flex items-start gap-4 p-4 bg-purple-50 rounded-lg border-l-4 border-purple-400">
                        <span class="bg-purple-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">D</span>
                        <div>
                          <strong class="text-purple-700">Desire:</strong>
                          <span class="ml-2">Show the value and benefits</span>
                        </div>
                      </div>
                      <div class="flex items-start gap-4 p-4 bg-green-50 rounded-lg border-l-4 border-green-400">
                        <span class="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">A</span>
                        <div>
                          <strong class="text-green-700">Action:</strong>
                          <span class="ml-2">Provide clear next steps</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="mb-8">
                    <h4 class="text-xl font-semibold mb-4 text-secondary-foreground">🎨 Visual Design Principles:</h4>
                    <ul class="space-y-3">
                      <li class="flex items-start gap-3">
                        <span class="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">•</span>
                        <span>Use consistent formatting and colors</span>
                      </li>
                      <li class="flex items-start gap-3">
                        <span class="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">•</span>
                        <span>Include plenty of white space</span>
                      </li>
                      <li class="flex items-start gap-3">
                        <span class="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">•</span>
                        <span>Choose readable fonts (14pt minimum)</span>
                      </li>
                      <li class="flex items-start gap-3">
                        <span class="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">•</span>
                        <span>Use high-quality images and graphics</span>
                      </li>
                      <li class="flex items-start gap-3">
                        <span class="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">•</span>
                        <span>Limit text per slide/page</span>
                      </li>
                    </ul>
                  </div>
                </div>
              `,
              worksheet: {
                title: "Content Creation Worksheet",
                fields: [
                  {
                    id: "content_outline",
                    label: "1. Create a high-level outline of your course content:",
                    type: "textarea",
                    placeholder: "Module 1: Introduction to...\nModule 2: Core concepts...\nModule 3: Practical application..."
                  },
                  {
                    id: "engagement_strategies",
                    label: "2. List 5 engagement strategies you'll use:",
                    type: "textarea",
                    placeholder: "1. Interactive polls\n2. Case studies\n3. Group discussions..."
                  },
                  {
                    id: "visual_elements",
                    label: "3. What visual elements will you include?",
                    type: "textarea",
                    placeholder: "Diagrams, infographics, videos, screenshots..."
                  }
                ]
              },
              resources: ['Content Creation Toolkit', 'Design Templates', 'Image Resources']
            },
            is_active: true
          },
          {
            id: 'step-2-content-dev',
            tutorial_id: tutorial.id,
            title: 'Assessment and Evaluation Strategies',
            description: 'Design effective assessments that measure learning outcomes',
            step_order: 2,
            content: { 
              html: `
                <div class="tutorial-content">
                  <h3 class="text-2xl font-bold mb-6 text-primary">Assessment and Evaluation Strategies</h3>
                  
                  <div class="mb-8">
                    <p class="text-lg mb-4 leading-relaxed">Effective assessment goes beyond testing—it's about measuring understanding, application, and growth throughout the learning journey.</p>
                  </div>
                  
                  <div class="mb-8">
                    <h4 class="text-xl font-semibold mb-4 text-secondary-foreground">📊 Types of Assessment:</h4>
                    <div class="grid gap-4">
                      <div class="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                        <strong class="text-blue-700 block mb-2">Formative Assessment</strong>
                        <p class="text-blue-600 text-sm">Ongoing evaluation during learning (polls, discussions, quick checks)</p>
                      </div>
                      <div class="p-4 bg-green-50 rounded-lg border-l-4 border-green-400">
                        <strong class="text-green-700 block mb-2">Summative Assessment</strong>
                        <p class="text-green-600 text-sm">Evaluation at the end of learning (exams, projects, portfolios)</p>
                      </div>
                      <div class="p-4 bg-purple-50 rounded-lg border-l-4 border-purple-400">
                        <strong class="text-purple-700 block mb-2">Authentic Assessment</strong>
                        <p class="text-purple-600 text-sm">Real-world applications and performance tasks</p>
                      </div>
                    </div>
                  </div>
                  
                  <div class="mb-8">
                    <h4 class="text-xl font-semibold mb-4 text-secondary-foreground">✅ Best Practices:</h4>
                    <ul class="space-y-3">
                      <li class="flex items-start gap-3">
                        <span class="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
                        <span>Align assessments with learning objectives</span>
                      </li>
                      <li class="flex items-start gap-3">
                        <span class="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
                        <span>Provide clear rubrics and expectations</span>
                      </li>
                      <li class="flex items-start gap-3">
                        <span class="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">3</span>
                        <span>Offer timely and constructive feedback</span>
                      </li>
                      <li class="flex items-start gap-3">
                        <span class="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">4</span>
                        <span>Use varied assessment methods</span>
                      </li>
                      <li class="flex items-start gap-3">
                        <span class="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">5</span>
                        <span>Include self and peer assessment opportunities</span>
                      </li>
                    </ul>
                  </div>
                </div>
              `,
              worksheet: {
                title: "Assessment Design Worksheet",
                fields: [
                  {
                    id: "assessment_types",
                    label: "1. What types of assessments will you use in your course?",
                    type: "textarea",
                    placeholder: "e.g., Quizzes, group projects, case study analysis, peer reviews..."
                  },
                  {
                    id: "evaluation_criteria",
                    label: "2. Define your evaluation criteria and rubric elements:",
                    type: "textarea",
                    placeholder: "e.g., Content mastery (40%), Application (30%), Participation (20%), Innovation (10%)..."
                  },
                  {
                    id: "feedback_strategy",
                    label: "3. How will you provide feedback to learners?",
                    type: "textarea",
                    placeholder: "e.g., Written comments, video feedback, peer feedback sessions, one-on-one meetings..."
                  }
                ]
              },
              resources: ['Assessment Rubric Templates', 'Feedback Best Practices Guide', 'Bloom\'s Taxonomy Reference']
            },
            is_active: true
          }
        ],
        'online-delivery': [
          {
            id: 'step-1-online',
            tutorial_id: tutorial.id,
            title: 'Platform Setup and Best Practices',
            description: 'Set up your online learning environment for optimal user experience',
            step_order: 1,
            content: { 
              html: `
                <div class="tutorial-content">
                  <h3 class="text-2xl font-bold mb-6 text-primary">Platform Setup and Best Practices</h3>
                  
                  <div class="mb-8">
                    <p class="text-lg mb-4 leading-relaxed">The right platform setup can make or break your online learning experience. Follow these guidelines to create a seamless environment.</p>
                  </div>
                  
                  <div class="mb-8">
                    <h4 class="text-xl font-semibold mb-4 text-secondary-foreground">⚙️ Technical Requirements:</h4>
                    <div class="grid gap-3">
                      <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <span>Reliable internet connection (minimum 10 Mbps upload)</span>
                      </div>
                      <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <span>Quality microphone and camera</span>
                      </div>
                      <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <span>Backup internet connection (mobile hotspot)</span>
                      </div>
                      <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <span>Updated browser and plugins</span>
                      </div>
                      <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <span>Quiet, well-lit space</span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="mb-8">
                    <div class="bg-green-50 border-l-4 border-green-400 p-6 rounded-r-lg">
                      <div class="flex items-center mb-2">
                        <span class="text-2xl mr-2">✅</span>
                        <strong class="text-green-800">Best Practice:</strong>
                      </div>
                      <p class="text-green-700 leading-relaxed">Always have a technical rehearsal 24 hours before your live session. Test all features you plan to use.</p>
                    </div>
                  </div>
                </div>
              `,
              worksheet: {
                title: "Online Delivery Setup Worksheet",
                fields: [
                  {
                    id: "platform_choice",
                    label: "1. Which platform will you use and why?",
                    type: "textarea",
                    placeholder: "e.g., Zoom for interactive features, Google Meet for simplicity..."
                  },
                  {
                    id: "technical_checklist",
                    label: "2. Create your pre-session technical checklist:",
                    type: "textarea",
                    placeholder: "• Test camera and microphone\n• Check internet speed\n• Prepare backup plan..."
                  },
                  {
                    id: "engagement_tools",
                    label: "3. What online engagement tools will you use?",
                    type: "textarea",
                    placeholder: "Polls, breakout rooms, screen annotation, chat..."
                  }
                ]
              },
              resources: ['Platform Comparison Guide', 'Technical Checklist', 'Troubleshooting Guide']
            },
            is_active: true
          }
        ],
        'evaluation-improvement': [
          {
            id: 'step-1-eval',
            tutorial_id: tutorial.id,
            title: 'Data Collection and Analysis',
            description: 'Implement effective methods to collect and analyze course feedback',
            step_order: 1,
            content: { 
              html: `
                <div class="tutorial-content">
                  <h3 class="text-2xl font-bold mb-6 text-primary">Data Collection and Analysis</h3>
                  
                  <div class="mb-8">
                    <p class="text-lg mb-4 leading-relaxed">Collecting meaningful data helps you understand what's working and what needs improvement in your courses.</p>
                  </div>
                  
                  <div class="mb-8">
                    <h4 class="text-xl font-semibold mb-4 text-secondary-foreground">📊 Types of Data to Collect:</h4>
                    <div class="grid gap-4">
                      <div class="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                        <strong class="text-blue-800">Reaction Data:</strong>
                        <span class="ml-2 text-blue-700">Participant satisfaction and engagement</span>
                      </div>
                      <div class="p-4 bg-green-50 rounded-lg border-l-4 border-green-400">
                        <strong class="text-green-800">Learning Data:</strong>
                        <span class="ml-2 text-green-700">Knowledge and skill acquisition</span>
                      </div>
                      <div class="p-4 bg-purple-50 rounded-lg border-l-4 border-purple-400">
                        <strong class="text-purple-800">Behavior Data:</strong>
                        <span class="ml-2 text-purple-700">Application of learning on the job</span>
                      </div>
                      <div class="p-4 bg-orange-50 rounded-lg border-l-4 border-orange-400">
                        <strong class="text-orange-800">Results Data:</strong>
                        <span class="ml-2 text-orange-700">Impact on performance and outcomes</span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="mb-8">
                    <h4 class="text-xl font-semibold mb-4 text-secondary-foreground">🔍 Collection Methods:</h4>
                    <div class="grid grid-cols-2 gap-3">
                      <div class="flex items-center gap-2 p-3 bg-gray-50 rounded">
                        <span class="w-2 h-2 bg-primary rounded-full"></span>
                        <span>Pre/post assessments</span>
                      </div>
                      <div class="flex items-center gap-2 p-3 bg-gray-50 rounded">
                        <span class="w-2 h-2 bg-primary rounded-full"></span>
                        <span>Exit tickets</span>
                      </div>
                      <div class="flex items-center gap-2 p-3 bg-gray-50 rounded">
                        <span class="w-2 h-2 bg-primary rounded-full"></span>
                        <span>Follow-up surveys</span>
                      </div>
                      <div class="flex items-center gap-2 p-3 bg-gray-50 rounded">
                        <span class="w-2 h-2 bg-primary rounded-full"></span>
                        <span>Focus groups</span>
                      </div>
                      <div class="flex items-center gap-2 p-3 bg-gray-50 rounded">
                        <span class="w-2 h-2 bg-primary rounded-full"></span>
                        <span>Observation checklists</span>
                      </div>
                      <div class="flex items-center gap-2 p-3 bg-gray-50 rounded">
                        <span class="w-2 h-2 bg-primary rounded-full"></span>
                        <span>Performance metrics</span>
                      </div>
                    </div>
                  </div>
                </div>
              `,
              worksheet: {
                title: "Evaluation Strategy Worksheet",
                fields: [
                  {
                    id: "evaluation_goals",
                    label: "1. What do you want to learn from your course evaluation?",
                    type: "textarea",
                    placeholder: "e.g., Effectiveness of content, participant satisfaction, learning outcomes..."
                  },
                  {
                    id: "data_collection_methods",
                    label: "2. Which data collection methods will you use?",
                    type: "textarea",
                    placeholder: "Pre-assessment, post-assessment, surveys, observations..."
                  },
                  {
                    id: "improvement_plan",
                    label: "3. How will you use the feedback to improve your course?",
                    type: "textarea",
                    placeholder: "Regular reviews, content updates, delivery method adjustments..."
                  }
                ]
              },
              resources: ['Evaluation Templates', 'Survey Tools', 'Analysis Framework']
            },
            is_active: true
          }
        ]
      };

      const steps = tutorialContent[tutorial.id as keyof typeof tutorialContent] || [
        {
          id: 'default-step',
          tutorial_id: tutorial.id,
          title: 'Welcome to the Tutorial',
          description: 'Get started with this comprehensive tutorial',
          step_order: 1,
          content: { html: '<p>Welcome! This tutorial will guide you through the essentials.</p>' },
          is_active: true
        }
      ];

      setSteps(steps);
      setProgress([]); // Empty progress for now
    } catch (error) {
      console.error('Error loading tutorial data:', error);
      toast({
        title: "Error",
        description: "Failed to load tutorial data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const evaluateWorksheet = async (stepId: string) => {
    const stepWorksheetData = worksheetData[stepId];
    if (!stepWorksheetData || Object.keys(stepWorksheetData).length === 0) {
      toast({
        title: "No Data",
        description: "Please fill out the worksheet before requesting evaluation",
        variant: "destructive"
      });
      return;
    }

    setIsEvaluating(prev => ({ ...prev, [stepId]: true }));
    
    try {
      const { data, error } = await supabase.functions.invoke('evaluate-worksheet', {
        body: {
          worksheetData: stepWorksheetData,
          tutorialId: tutorial.id,
          stepId: stepId
        }
      });

      if (error) throw error;

      setEvaluations(prev => ({
        ...prev,
        [stepId]: data.evaluation
      }));

      console.log('Received evaluation data:', JSON.stringify(data.evaluation, null, 2));

      toast({
        title: "Evaluation Complete",
        description: `Your work has been evaluated with a score of ${data.evaluation.overallScore}/10`,
      });
    } catch (error) {
      console.error('Error evaluating worksheet:', error);
      toast({
        title: "Evaluation Failed",
        description: "Unable to evaluate your worksheet. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsEvaluating(prev => ({ ...prev, [stepId]: false }));
    }
  };

  const updateWorksheetField = (stepId: string, fieldId: string, value: string) => {
    setWorksheetData(prev => ({
      ...prev,
      [stepId]: {
        ...(prev[stepId] && typeof prev[stepId] === 'object' ? prev[stepId] : {}),
        [fieldId]: value
      }
    }));
  };

  const toggleStepCompletion = async (stepId: string) => {
    try {
      const isCompleted = progress.some(p => p.step_id === stepId && p.completed_at);

      if (isCompleted) {
        setProgress(prev => prev.map(p => 
          p.step_id === stepId ? { ...p, completed_at: null } : p
        ));
      } else {
        const newProgress = {
          id: `progress-${Date.now()}`,
          user_id: 'current-user',
          tutorial_id: tutorial.id,
          step_id: stepId,
          completed_at: new Date().toISOString()
        };
        setProgress(prev => [...prev, newProgress]);
      }

      toast({
        title: "Progress Updated",
        description: isCompleted ? "Step marked as incomplete" : "Step completed!",
      });

      // Notify parent component of progress change
      if (onProgressUpdate) {
        const completedSteps = progress
          .filter(p => p.completed_at)
          .map(p => p.step_id);
        if (!isCompleted && !completedSteps.includes(stepId)) {
          completedSteps.push(stepId);
        } else if (isCompleted) {
          const index = completedSteps.indexOf(stepId);
          if (index > -1) {
            completedSteps.splice(index, 1);
          }
        }
        onProgressUpdate(tutorial.id, completedSteps);
      }
    } catch (error) {
      console.error('Error updating progress:', error);
      toast({
        title: "Error",
        description: "Failed to update progress",
        variant: "destructive"
      });
    }
  };

  const downloadWorksheet = (stepId: string) => {
    const stepData = worksheetData[stepId];
    const evaluation = evaluations[stepId];
    const step = steps.find(s => s.id === stepId);
    
    if (!stepData || !step) return;

    // Create formatted HTML content
    let content = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${tutorial.title} - ${step.title}</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
            color: #333;
        }
        .header {
            text-align: center;
            border-bottom: 3px solid #2563eb;
            padding-bottom: 20px;
            margin-bottom: 30px;
        }
        .title {
            font-size: 24px;
            font-weight: bold;
            margin: 0 0 10px 0;
            color: #1e40af;
        }
        .subtitle {
            font-size: 18px;
            color: #6b7280;
            margin: 5px 0;
        }
        .date {
            font-size: 14px;
            color: #9ca3af;
        }
        .section {
            margin: 30px 0;
            padding: 20px;
            border-radius: 8px;
            background-color: #f8fafc;
            border-left: 4px solid #2563eb;
        }
        .section-title {
            font-size: 18px;
            font-weight: bold;
            color: #1e40af;
            margin-bottom: 15px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }
        .field {
            margin: 20px 0;
        }
        .field-name {
            font-weight: bold;
            color: #374151;
            margin-bottom: 8px;
            text-transform: uppercase;
            font-size: 14px;
        }
        .field-value {
            background: white;
            padding: 15px;
            border-radius: 6px;
            border: 1px solid #e5e7eb;
            white-space: pre-wrap;
            line-height: 1.8;
        }
        .score-badge {
            display: inline-block;
            padding: 8px 16px;
            border-radius: 20px;
            font-weight: bold;
            font-size: 16px;
            color: white;
            background: linear-gradient(135deg, #10b981, #059669);
        }
        .feedback-item {
            background: white;
            padding: 20px;
            border-radius: 8px;
            margin: 15px 0;
            border: 1px solid #e5e7eb;
        }
        .feedback-title {
            font-weight: bold;
            color: #1f2937;
            margin-bottom: 10px;
            text-transform: capitalize;
        }
        .suggestions {
            background: #fef3c7;
            padding: 20px;
            border-radius: 8px;
            border-left: 4px solid #f59e0b;
        }
        .suggestion-item {
            margin: 8px 0;
            padding-left: 10px;
        }
        @media print {
            body { padding: 0; }
            .section { break-inside: avoid; }
        }
    </style>
</head>
<body>
    <div class="header">
        <h1 class="title">${tutorial.title}</h1>
        <h2 class="subtitle">${step.title}</h2>
        <p class="date">Completed: ${new Date().toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })}</p>
    </div>
    
    <div class="section">
        <div class="section-title">📝 Worksheet Responses</div>
        ${Object.entries(stepData).map(([key, value]) => {
          const fieldName = key.replace(/_/g, ' ').toUpperCase();
          return `
            <div class="field">
                <div class="field-name">${fieldName}</div>
                <div class="field-value">${String(value).replace(/\n/g, '<br>')}</div>
            </div>
          `;
        }).join('')}
    </div>`;
    
    if (evaluation) {
      content += `
    <div class="section">
        <div class="section-title">🤖 AI Evaluation Results</div>
        <p><strong>Overall Score:</strong> <span class="score-badge">${evaluation.overallScore}/10</span></p>
        
        <div class="field">
            <div class="field-name">Summary</div>
            <div class="field-value">${evaluation.summary}</div>
        </div>
        
        ${evaluation.feedback ? `
            <div class="field-name" style="margin-top: 25px;">Detailed Feedback</div>
            ${Object.entries(evaluation.feedback).map(([key, feedback]) => {
              const fieldName = key.replace(/_/g, ' ').toUpperCase();
              // Format feedback with proper line breaks and clean spacing
              const cleanedFeedback = String(feedback)
                .replace(/\s+/g, ' ')  // Replace multiple spaces with single space
                .replace(/\n+/g, ' ')  // Replace line breaks with spaces
                .trim();
              
              // Split into proper sentences
              const sentences = cleanedFeedback
                .split(/(?<=[.!?])\s+(?=[A-Z])/)
                .filter(sentence => sentence.trim().length > 0);
              
              // Group into paragraphs
              const paragraphs = [];
              let currentParagraph = [];
              
              sentences.forEach((sentence, i) => {
                const trimmedSentence = sentence.trim();
                
                // Start new paragraph for certain keywords
                if (trimmedSentence.match(/^(For example|Similarly|Consider|However|Additionally|Furthermore|While|The plan)/i) && currentParagraph.length > 0) {
                  paragraphs.push(currentParagraph.join(' '));
                  currentParagraph = [];
                }
                
                currentParagraph.push(trimmedSentence);
                
                // End paragraph after 2-3 sentences
                if (currentParagraph.length >= 3 || i === sentences.length - 1) {
                  paragraphs.push(currentParagraph.join(' '));
                  currentParagraph = [];
                }
              });
              
              const formattedFeedback = paragraphs
                .filter(p => p.trim())
                .map(paragraph => `<p style="margin-bottom: 12px; line-height: 1.6;">${paragraph.trim()}</p>`)
                .join('');
              
              // Get corresponding suggestions
              const suggestions = evaluation.suggestions && evaluation.suggestions[key] ? 
                evaluation.suggestions[key].map((suggestion, i) => 
                  `<li style="margin: 5px 0; padding-left: 10px;">${suggestion}</li>`
                ).join('') : '';
              
              return `
                <div class="feedback-item">
                    <div class="feedback-title">${fieldName}</div>
                    <div>${formattedFeedback}</div>
                    ${suggestions ? `
                        <div style="margin-top: 15px; padding: 15px; background: #f0f9ff; border-radius: 6px; border-left: 4px solid #0ea5e9;">
                            <div style="font-weight: bold; color: #0c4a6e; margin-bottom: 10px;">🎯 Suggestions for Improvement:</div>
                            <ul style="margin: 0; padding-left: 20px; color: #0c4a6e;">${suggestions}</ul>
                        </div>
                    ` : ''}
                </div>
              `;
            }).join('')}
        ` : ''}
        
        ${evaluation.suggestions && evaluation.suggestions.length > 0 ? `
            <div class="suggestions">
                <div class="field-name">💡 Suggestions for Improvement</div>
                ${evaluation.suggestions.map((suggestion: string, index: number) => 
                  `<div class="suggestion-item">${index + 1}. ${suggestion}</div>`
                ).join('')}
            </div>
        ` : ''}
        
        <p style="margin-top: 20px;"><strong>Meets Standards:</strong> ${evaluation.passesStandard ? '✅ Yes' : '❌ No'}</p>
    </div>`;
    }

    content += `
</body>
</html>`;

    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tutorial.title.replace(/\s+/g, '_')}-${step.title.replace(/\s+/g, '_')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "Download Complete",
      description: "Your worksheet has been downloaded as a formatted HTML document",
    });
  };

  const downloadAllWorksheets = () => {
    // Create Word-compatible HTML content
    let content = `
<html xmlns:o="urn:schemas-microsoft-com:office:office" 
      xmlns:w="urn:schemas-microsoft-com:office:word" 
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
    <meta charset="utf-8">
    <title>${tutorial.title} - Complete Report</title>
    <style>
        @page { 
            margin: 1in; 
            size: 8.5in 11in; 
        }
        body { 
            font-family: 'Calibri', 'Arial', sans-serif; 
            font-size: 11pt; 
            line-height: 1.5; 
            margin: 0; 
            padding: 0;
        }
        .header { 
            text-align: center; 
            margin-bottom: 30px; 
            border-bottom: 2pt solid #2563eb; 
            padding-bottom: 20px; 
        }
        .title { 
            font-size: 18pt; 
            font-weight: bold; 
            color: #1e40af; 
            margin: 0 0 8px 0; 
        }
        .subtitle { 
            font-size: 14pt; 
            color: #64748b; 
            margin: 5px 0; 
        }
        .date { 
            font-size: 10pt; 
            color: #9ca3af; 
        }
        .step-section { 
            margin: 25px 0; 
            page-break-inside: avoid; 
        }
        .step-title { 
            font-size: 14pt; 
            font-weight: bold; 
            color: #1e40af; 
            margin-bottom: 15px; 
            border-bottom: 1pt solid #e2e8f0; 
            padding-bottom: 5px; 
        }
        .section-header { 
            font-size: 12pt; 
            font-weight: bold; 
            color: #374151; 
            margin: 15px 0 10px 0; 
        }
        .response-item { 
            margin: 12px 0; 
            border-left: 3pt solid #10b981; 
            padding-left: 12px; 
        }
        .response-label { 
            font-weight: bold; 
            color: #1f2937; 
            margin-bottom: 5px; 
            font-size: 10pt; 
        }
        .response-text { 
            color: #4b5563; 
            margin-bottom: 10px; 
        }
        .evaluation-section { 
            background-color: #fef3c7; 
            padding: 15px; 
            margin: 15px 0; 
            border: 1pt solid #f59e0b; 
        }
        .score-display { 
            font-size: 14pt; 
            font-weight: bold; 
            color: #92400e; 
            text-align: center; 
            margin: 10px 0; 
        }
        .no-data { 
            text-align: center; 
            color: #9ca3af; 
            font-style: italic; 
            padding: 20px; 
        }
    </style>
</head>
<body>
    <div class="header">
        <div class="title">${tutorial.title}</div>
        <div class="subtitle">Complete Worksheets Report</div>
        <div class="date">Generated: ${new Date().toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}</div>
    </div>`;

    steps.forEach((step, index) => {
      const stepData = worksheetData[step.id] || {};
      const evaluation = evaluations[step.id];
      
      content += `
    <div class="step-section">
        <div class="step-title">${index + 1}. ${step.title}</div>`;
        
      if (Object.keys(stepData).length > 0) {
        content += `
        <div class="section-header">Worksheet Responses</div>`;
            
        Object.entries(stepData).forEach(([key, value]) => {
          const fieldName = key.replace(/_/g, ' ').toUpperCase();
          // Clean text for Word compatibility
          const cleanValue = String(value)
            .replace(/\n/g, '<br>')
            .replace(/'/g, '&apos;')
            .replace(/"/g, '&quot;');
          
          content += `
        <div class="response-item">
            <div class="response-label">${fieldName}</div>
            <div class="response-text">${cleanValue}</div>
        </div>`;
        });
        
        if (evaluation) {
          const cleanSummary = String(evaluation.summary)
            .replace(/'/g, '&apos;')
            .replace(/"/g, '&quot;');
            
          content += `
        <div class="evaluation-section">
            <div class="section-header">AI Evaluation Results</div>
            <div class="score-display">Overall Score: ${evaluation.overallScore}/10</div>
            <div class="response-text">
                <strong>Summary:</strong> ${cleanSummary}<br><br>
                <strong>Meets Standards:</strong> ${evaluation.passesStandard ? 'Yes' : 'No'}
            </div>
        </div>`;
        }
      } else {
        content += `
        <div class="no-data">No responses recorded for this step</div>`;
      }
      
      content += `</div>`;
    });

    content += `
    <div style="margin-top: 40px; text-align: center; color: #6b7280; font-size: 10pt; border-top: 1pt solid #e5e7eb; padding-top: 20px;">
        <p>This report was generated by the Course Creation Academy</p>
        <p>Continue your instructional design journey at your learning portal</p>
    </div>
</body>
</html>`;

    // Create Word-compatible blob
    const blob = new Blob([content], { 
      type: 'application/vnd.ms-word'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tutorial.title.replace(/\s+/g, '_')}-Complete_Report.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "Download Complete",
      description: "Complete worksheet report has been downloaded as a Word document",
    });
  };

  const renderWorksheet = (step: TutorialStep) => {
    const worksheet = step.content?.worksheet;
    if (!worksheet) return null;

    const stepWorksheetData = worksheetData[step.id] || {};
    const stepEvaluation = evaluations[step.id];

    return (
      <div className="mt-8 border-t pt-8">
        <div className="mb-6">
          <h4 className="text-xl font-semibold mb-2 flex items-center gap-2">
            <Edit3 className="h-5 w-5" />
            {worksheet.title}
          </h4>
          <p className="text-muted-foreground">Complete the worksheet below to practice what you've learned:</p>
        </div>

        <div className="space-y-6">
          {worksheet.fields.map((field: any) => (
            <div key={field.id} className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                {field.label}
              </label>
              {field.type === 'textarea' ? (
                <Textarea
                  placeholder={field.placeholder}
                  value={stepWorksheetData[field.id] || ''}
                  onChange={(e) => updateWorksheetField(step.id, field.id, e.target.value)}
                  rows={4}
                  className="resize-none"
                />
              ) : (
                <input
                  type="text"
                  placeholder={field.placeholder}
                  value={stepWorksheetData[field.id] || ''}
                  onChange={(e) => updateWorksheetField(step.id, field.id, e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                />
              )}
            </div>
          ))}
        </div>

        <Separator className="my-6" />

        <div className="flex gap-3">
          <Button
            onClick={() => evaluateWorksheet(step.id)}
            disabled={isEvaluating[step.id]}
            className="gap-2"
          >
            <Brain className="h-4 w-4" />
            {isEvaluating[step.id] ? 'Evaluating...' : 'Get AI Feedback'}
          </Button>
          
          <Button
            variant="outline"
            onClick={() => downloadWorksheet(step.id)}
            disabled={!stepWorksheetData || Object.keys(stepWorksheetData).length === 0}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Download Worksheet
          </Button>
        </div>

        {stepEvaluation && (
          <div className="mt-8 p-6 bg-muted rounded-lg">
            <div className="flex items-center gap-2 mb-4">
              <Brain className="h-5 w-5 text-primary" />
              <h5 className="font-semibold">AI Evaluation Results</h5>
              <Badge className={stepEvaluation.overallScore >= 8 ? 'bg-green-100 text-green-800' : 
                               stepEvaluation.overallScore >= 6 ? 'bg-yellow-100 text-yellow-800' : 
                               'bg-red-100 text-red-800'}>
                Score: {stepEvaluation.overallScore}/10
              </Badge>
            </div>

            <div className="space-y-4">
              <div>
                <h6 className="font-medium mb-2">Summary:</h6>
                <p className="text-sm text-muted-foreground">{stepEvaluation.summary}</p>
              </div>

              <div>
                <h6 className="font-medium mb-2">Score Breakdown:</h6>
                <div className="bg-background rounded-lg p-3 border text-sm">
                  <div className="grid grid-cols-3 gap-4 mb-2">
                    <div className="text-center">
                      <div className="font-medium text-green-600">8-10 Points</div>
                      <div className="text-xs text-muted-foreground">Excellent</div>
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-yellow-600">6-7 Points</div>
                      <div className="text-xs text-muted-foreground">Good</div>
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-red-600">0-5 Points</div>
                      <div className="text-xs text-muted-foreground">Needs Improvement</div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground border-t pt-2">
                    Scoring is based on completeness, clarity, specificity, and alignment with instructional design best practices.
                  </div>
                </div>
              </div>

              {stepEvaluation.feedback && Object.keys(stepEvaluation.feedback).length > 0 && (
                  <div>
                    <h6 className="font-medium mb-2">Detailed Feedback:</h6>
                    <div className="space-y-4">
                      {Object.entries(stepEvaluation.feedback).map(([key, feedback]: [string, any]) => {
                        console.log('Processing feedback entry:', key, typeof feedback, feedback);
                        
                        const renderFeedbackContent = (feedback: any) => {
                          // Handle string feedback
                          if (typeof feedback === 'string') {
                            // Check if it's a JSON string
                            if (feedback.trim().startsWith('{') && feedback.trim().endsWith('}')) {
                              try {
                                const parsed = JSON.parse(feedback);
                                return renderFeedbackContent(parsed);
                              } catch {
                                // If parsing fails, treat as regular text with better formatting
                                return (
                                  <div className="text-sm text-foreground leading-relaxed space-y-3">
                                    {feedback
                                      .split(/(?=Module \d+:|Similarly,|For example,|Consider also|Think about)/i)
                                      .filter(chunk => chunk.trim())
                                      .map((chunk, i) => (
                                        <p key={i} className="mb-3">{chunk.trim()}</p>
                                      ))
                                    }
                                  </div>
                                );
                              }
                            } else {
                              // Regular text content with improved formatting
                              return (
                                <div className="text-sm text-foreground leading-relaxed space-y-4">
                                  {(() => {
                                    // Clean up the text first
                                    const cleanedText = feedback
                                      .replace(/\s+/g, ' ')  // Replace multiple spaces with single space
                                      .replace(/\n+/g, ' ')  // Replace line breaks with spaces
                                      .trim();
                                    
                                    // Split into sentences more carefully
                                    const sentences = cleanedText
                                      .split(/(?<=[.!?])\s+(?=[A-Z])/)
                                      .filter(sentence => sentence.trim().length > 0);
                                    
                                    // Group sentences into logical paragraphs
                                    const paragraphs = [];
                                    let currentParagraph = [];
                                    
                                    sentences.forEach((sentence, i) => {
                                      const trimmedSentence = sentence.trim();
                                      
                                      // Start new paragraph for certain keywords
                                      if (trimmedSentence.match(/^(For example|Similarly|Consider|However|Additionally|Furthermore|While|The plan|Think about)/i) && currentParagraph.length > 0) {
                                        if (currentParagraph.length > 0) {
                                          paragraphs.push(currentParagraph.join(' '));
                                          currentParagraph = [];
                                        }
                                      }
                                      
                                      currentParagraph.push(trimmedSentence);
                                      
                                      // End paragraph after 2-3 sentences or at natural breaks
                                      if (currentParagraph.length >= 3 || 
                                          i === sentences.length - 1 ||
                                          trimmedSentence.match(/\.(?: Consider| Think about| For example)/i)) {
                                        paragraphs.push(currentParagraph.join(' '));
                                        currentParagraph = [];
                                      }
                                    });
                                    
                                    // Add any remaining sentences
                                    if (currentParagraph.length > 0) {
                                      paragraphs.push(currentParagraph.join(' '));
                                    }
                                    
                                    return paragraphs.map((paragraph, i) => {
                                      const cleanParagraph = paragraph.trim();
                                      
                                      // Check if this is a suggestion
                                      if (cleanParagraph.match(/^(Consider|Think about|Try to|You could|It would be better|Adding|Include)/i)) {
                                        return (
                                          <div key={i} className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r-lg">
                                            <div className="text-blue-800 text-xs font-semibold mb-1">💡 SUGGESTION</div>
                                            <p className="text-blue-700">{cleanParagraph}</p>
                                          </div>
                                        );
                                      }
                                      
                                      // Regular feedback paragraph
                                      return <p key={i} className="mb-3 leading-relaxed">{cleanParagraph}</p>;
                                    });
                                  })()}
                                </div>
                              );
                            }
                          }
                          
                          // Handle object feedback
                          if (typeof feedback === 'object' && feedback !== null) {
                            return (
                              <div className="space-y-4">
                                {Object.entries(feedback).map(([subKey, subValue]: [string, any]) => (
                                  <div key={subKey} className="bg-background rounded-lg p-4 border border-border">
                                    <div className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wide">
                                      {subKey.replace(/_/g, ' ')}
                                    </div>
                                    <div className="text-sm text-muted-foreground leading-relaxed space-y-2">
                                      {typeof subValue === 'string' 
                                        ? subValue.split(/\. (?=[A-Z])/).filter(p => p.trim()).map((sentence, i) => (
                                            <p key={i}>{sentence.trim()}{sentence.trim().endsWith('.') ? '' : '.'}</p>
                                          ))
                                        : <p>{String(subValue)}</p>
                                      }
                                    </div>
                                    
                                    {/* Check for corresponding suggestions */}
                                    {stepEvaluation.suggestions && stepEvaluation.suggestions[subKey] && (
                                      <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                                        <div className="font-semibold text-green-800 mb-3 text-sm">🎯 Suggestions for Improvement:</div>
                                        <ul className="space-y-2">
                                          {stepEvaluation.suggestions[subKey].map((suggestion: string, i: number) => (
                                            <li key={i} className="text-green-700 text-sm flex items-start gap-2">
                                              <span className="text-green-500 mt-0.5 font-bold">•</span>
                                              <span>{suggestion}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          
                          return (
                            <div className="text-sm text-foreground">
                              {String(feedback)}
                            </div>
                          );
                        };

                        return (
                          <div key={key} className="bg-muted/30 rounded-lg p-4 border-l-4 border-primary">
                            <div className="font-bold text-primary capitalize mb-3 text-base">
                              {key.replace(/_/g, ' ')}
                            </div>
                            {renderFeedbackContent(feedback)}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              {stepEvaluation.suggestions && stepEvaluation.suggestions.length > 0 && (
                <div>
                  <h6 className="font-medium mb-2">Suggestions for Improvement:</h6>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    {stepEvaluation.suggestions.map((suggestion: string, index: number) => (
                      <li key={index} className="list-disc">{suggestion}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const goToNextStep = () => {
    if (currentStepIndex < steps.length - 1) {
      const nextIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextIndex);
      setActiveAccordionValue(steps[nextIndex]?.id);
      
      toast({
        title: "Next Step",
        description: `Proceeding to ${steps[nextIndex]?.title}`,
      });
    }
  };

  const getProgressPercentage = () => {
    if (steps.length === 0) return 0;
    const completedCount = progress.filter(p => p.completed_at).length;
    return Math.min((completedCount / steps.length) * 100, 100);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-4xl">
          <CardContent className="p-6">
            <div className="text-center">Loading tutorial...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex justify-between items-start">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <CardTitle className="text-2xl">{tutorial.title}</CardTitle>
                <Badge className={getDifficultyColor(tutorial.difficulty)}>
                  {tutorial.difficulty.toUpperCase()}
                </Badge>
              </div>
              <p className="text-muted-foreground">{tutorial.description}</p>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {tutorial.duration_minutes} min
                </div>
                <Badge variant="outline">{tutorial.category}</Badge>
              </div>
            </div>
            <Button variant="ghost" onClick={onClose}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Overall Progress</span>
              <span>{Math.round(getProgressPercentage())}%</span>
            </div>
            <Progress value={getProgressPercentage()} className="h-2" />
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <Accordion 
            type="single" 
            collapsible 
            value={activeAccordionValue} 
            onValueChange={setActiveAccordionValue}
            className="w-full"
          >
            {steps.map((step, index) => {
              const isCompleted = progress.some(p => p.step_id === step.id && p.completed_at);
              return (
                <AccordionItem key={step.id} value={step.id}>
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        {isCompleted ? (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : (
                          <div className="w-5 h-5 border-2 border-muted-foreground rounded-full flex items-center justify-center">
                            <span className="text-xs font-medium">{index + 1}</span>
                          </div>
                        )}
                        <span className="font-medium">Step {index + 1}: {step.title}</span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pl-8">
                      <p className="text-muted-foreground">{step.description}</p>
                      
                      {step.content && (
                        <div className="prose prose-sm max-w-none">
                          <div dangerouslySetInnerHTML={{ __html: step.content.html || step.content }} />
                        </div>
                      )}
                      
                      {renderWorksheet(step)}
                      
                      <div className="flex gap-2 pt-4">
                        <Button 
                          variant={isCompleted ? "secondary" : "default"}
                          onClick={() => toggleStepCompletion(step.id)}
                        >
                          {isCompleted ? 'Mark Incomplete' : 'Mark Complete'}
                        </Button>
                        
                        {step.content?.videoUrl && (
                          <Button variant="outline" asChild>
                            <a href={step.content.videoUrl} target="_blank" rel="noopener noreferrer">
                              <Play className="h-4 w-4 mr-2" />
                              Watch Video
                            </a>
                          </Button>
                        )}
                        
                        {index === steps.length - 1 && (
                          <Button 
                            variant="outline"
                            onClick={downloadAllWorksheets}
                            className="gap-2"
                          >
                            <FileDown className="h-4 w-4" />
                            Download All Work
                          </Button>
                        )}
                        
                        {index < steps.length - 1 && (
                          <Button 
                            variant="outline"
                            onClick={goToNextStep}
                            className="gap-2"
                          >
                            <ArrowRight className="h-4 w-4" />
                            Next Step
                          </Button>
                        )}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}